import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { get_api_url } from '../../../../utils/urls';
import { post_api } from '../../../../redux/api_funcs';

// PROD-8055: move a member's next billing date. transaction.next_billing_date is a UTC column and
// adjust_membership_date writes the string through verbatim, so the picked wall-clock is sent as-is
// and the field is labelled UTC rather than converted from the CMS user's timezone.
function ChangeNextBillingDate({
  student_uuid,
  current_next_billing_date,
  onClose,
  onSuccess,
  notifySuccess,
  notifyFailure
}) {
  const [next_billing_date, set_next_billing_date] = useState(null);
  const [reason, set_reason] = useState('');
  const [loading, set_loading] = useState(false);

  const submit_disabled = loading || !next_billing_date || reason.trim().length === 0;

  // The picked wall-clock is stored as UTC, so the floor is current UTC rendered as a local Date.
  // Using plain `new Date()` would block times that are still in the future in UTC for anyone east
  // of it — a 15:00 IST user could not enter 10:00 UTC.
  const earliest = moment(moment.utc().format('YYYY-MM-DD HH:mm:ss')).toDate();

  const handle_submit = () => {
    const url = get_api_url('adjust_membership_date');
    const payload = {
      student_uuid,
      next_billing_date: moment(next_billing_date).format('YYYY-MM-DD HH:mm:ss'),
      reason: reason.trim()
    };
    set_loading(true);
    post_api(url, payload, true)
      .then(() => {
        onClose();
        notifySuccess('Next billing date updated successfully.');
        onSuccess();
      })
      .catch((e) => {
        set_loading(false);
        notifyFailure(e, 'Failed to update next billing date. Please try again.');
      });
  };

  return (
    <div className="flex flex-col gap-4">
      <div
        className="flex items-baseline justify-between gap-3 rounded px-3 py-2 text-sm"
        style={{ background: 'var(--surface-sunken)' }}
      >
        <span style={{ color: 'var(--text-muted)' }}>Current next billing date</span>
        <span className="font-semibold">{current_next_billing_date || 'Not set'}</span>
      </div>

      <label className="flex flex-col gap-1">
        <span className="text-sm font-medium">
          New billing date <span style={{ color: 'var(--state-error)' }}>*</span>
          <span className="ml-1 font-normal" style={{ color: 'var(--text-muted)' }}>
            (UTC)
          </span>
        </span>
        <DatePicker
          selected={next_billing_date}
          onChange={(val) => set_next_billing_date(val)}
          showTimeSelect
          timeFormat="HH:mm"
          timeIntervals={15}
          dateFormat="yyyy-MM-dd HH:mm"
          placeholderText="Select date and time"
          minDate={earliest}
          filterTime={(time) => time.getTime() >= earliest.getTime()}
          // CustomModal's inner container is overflow:auto, which clips the popup — portal it out.
          portalId="datepicker-portal"
          wrapperClassName="w-full"
          className="w-full"
        />
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
          Saved as entered, in UTC — not converted from your timezone. Past dates and times are
          disabled.
        </span>
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-sm font-medium">
          Reason <span style={{ color: 'var(--state-error)' }}>*</span>
        </span>
        <textarea
          name="change_billing_date_reason"
          value={reason}
          onChange={(e) => set_reason(e.target.value)}
          rows={3}
          className="w-full"
          style={{ resize: 'vertical' }}
          placeholder="Why is the billing date being changed?"
        />
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
          Recorded on the student&apos;s timeline against your name.
        </span>
      </label>

      <div
        className="flex justify-end gap-2 pt-3"
        style={{ borderTop: '1px solid var(--border-default)' }}
      >
        <button type="button" className="button-secondary" onClick={onClose}>
          Cancel
        </button>
        {/* Native disabled for keyboard/AT, button-disabled for the greyed-out look. */}
        <button
          type="button"
          className={submit_disabled ? 'button-disabled' : ''}
          onClick={handle_submit}
          disabled={submit_disabled}
        >
          {loading ? 'Submitting...' : 'Submit'}
        </button>
      </div>
    </div>
  );
}

export default ChangeNextBillingDate;
